import { ReactElement, useState } from 'react';
import { Component as CaretDownIcon } from 'icons/icon-ui--caret-outline.svg';
import { Component as CaretUpIcon } from 'icons/icon-ui--caret-up-outline.svg';
import styles from './PriceSummary.module.scss';
import { CouponBanner } from './CouponBanner';

type Props = {
  subTotal: string;
  shipping: string;
  extendedAreaSurcharge: string;
  discounts?: string;
  couponAmount?: string;
  couponCode?: string;
  setScrollToBottom?: React.Dispatch<React.SetStateAction<boolean>>;
  isCouponApplied?: boolean;
  applyUserCoupon?: (string) => void;
  userCouponCode?: string;
  setUserCouponCode: (string) => void;
  couponErrorMessage?: string;
};

export function PriceSummary({
  subTotal,
  shipping,
  extendedAreaSurcharge,
  discounts,
  couponAmount,
  couponCode,
  setScrollToBottom,
  isCouponApplied = false,
  applyUserCoupon = () => undefined,
  userCouponCode = '',
  setUserCouponCode = () => undefined,
  couponErrorMessage,
}: Props): ReactElement {
  const [showPriceSummary, setShowPriceSummary] = useState(true);
  const [showAddCoupon, setShowAddCoupon] = useState(false);
  const isLoggedIn = !document.cookie.includes('is_prospect_user=true');
  const toggleShowPriceSummary = () => setShowPriceSummary((prev) => !prev);
  const toggleShowAddCoupon = () => setShowAddCoupon((prev) => !prev);
  const PriceSummaryIcon = showPriceSummary ? CaretUpIcon : CaretDownIcon;
  const showDiscounts = discounts !== undefined;
  const showCouponText = couponAmount != null && couponCode != null && isCouponApplied
    && couponErrorMessage === '';
  const showExtendedAreaSurcharge = (isLoggedIn && !!extendedAreaSurcharge) || !isLoggedIn;

  return (
    <div className={styles.price_summary_container}>
      <div className={styles.price_summary_toggle}>
        <div
          onClick={() => {
            toggleShowPriceSummary();
            setScrollToBottom?.((prev) => !prev);
          }}
          role="button"
          aria-labelledby="price-summary-button"
          onKeyPress={toggleShowPriceSummary}
          tabIndex={0}
          className={styles.price_summary_item}
        >
          <span>Price Summary</span>
          <PriceSummaryIcon className="c-svg-fill-primary-color" width="12" focusable="false" />
        </div>
      </div>
      <hr />
      {showPriceSummary && (
        <div className="price-summary">
          <div className={styles.price_summary_breakdown}>
            <div className={styles.price_summary_item}>
              <span>Sub Total:</span>
              <span>{subTotal}</span>
            </div>
            <div className={styles.price_summary_item}>
              <span>Shipping:</span>
              <span>{shipping}</span>
            </div>
            {showExtendedAreaSurcharge && (
              <div className={styles.price_summary_item}>
                <span>Extended Area Surcharge:</span>
                <span>{isLoggedIn ? extendedAreaSurcharge : 'TBD'}</span>
              </div>
            )}
            {!isLoggedIn
              && (
              <>
                <div
                  onClick={() => {
                    toggleShowAddCoupon();
                  }}
                  role="button"
                  aria-labelledby="coupon-add-button"
                  onKeyPress={toggleShowAddCoupon}
                  tabIndex={0}
                  className={styles.add_coupon_text}
                >
                  <span>
                    {showAddCoupon ? '-' : '+'}
                    {' '}
                    Apply a discount or gift code
                  </span>
                </div>
                {showAddCoupon && (
                <div className={styles.add_coupon_row}>
                  <input
                    name="add-coupon-input"
                    value={userCouponCode?.toUpperCase()}
                    onChange={(e) => setUserCouponCode(e.target.value)}
                  />
                  <div
                    onClick={() => { applyUserCoupon(userCouponCode); }}
                    role="button"
                    aria-labelledby="coupon-add-button"
                    onKeyPress={applyUserCoupon}
                    tabIndex={0}
                    className={styles.coupon_save_button}
                  >
                    <span>Save</span>
                  </div>
                </div>
                )}
              </>
              )}

            {showDiscounts && (
              <div className={styles.price_summary_item}>
                <span>Discounts:</span>
                <span>{discounts}</span>
              </div>
            )}
          </div>
          {showCouponText
            && (
              <>
                <hr />
                <CouponBanner couponAmount={couponAmount} couponCode={couponCode} />
              </>
            )}
          {couponErrorMessage && (
          <>
            <hr />
            <div className={styles.coupon_error_text}>
              <b>{userCouponCode?.toUpperCase()}</b>
              {' '}
              &nbsp;
              {couponErrorMessage}
            </div>
          </>
          )}
        </div>
      )}
    </div>
  );
}
